import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Auth } from '@shared/auth';
import { CookieService } from 'ngx-cookie';
import { Observable } from 'rxjs/Observable';
import { TRACKING_CONFIG } from './tracking.config';
import { TrackingConfig } from './tracking.interface';

export type TrackingEventName =
  | 'page_view'
  | 'page_performance_timings'
  | 'user_timing'
  | 'user_action';

// Common fields added to all tracking events
export interface TrackingEvent {
  en: TrackingEventName; // Event category
  acct: string; // App ID / "account"
  location: string; // Target URL (complete URL at the end of the interaction)
  session_id: string; // Unique ID for the session
  t: string; // Timestamp (Unix)
  user_id?: number; // User ID if the user is logged-in
}

export interface TrackingEventData {
  [k: string]: any;
}

@Injectable()
export class Tracking {
  sessionId: string;
  readonly MAX_GET_LENGTH = 2000;
  sequenceNum = 0;

  constructor(
    private http: HttpClient,
    private auth: Auth,
    private cookies: CookieService,
    @Inject(TRACKING_CONFIG) private config: TrackingConfig,
  ) {
    let sessionId = this.cookies.get(this.config.sessionCookie);
    if (!sessionId) {
      // Generate a new one if not.
      sessionId = this.generateSessionId();
      this.cookies.put('_tracking_session', sessionId, {
        expires: new Date(new Date().setFullYear(new Date().getFullYear() + 1)),
      });
    }
    this.sessionId = sessionId;

    // send the event backlog if any
    try {
      const backlog = localStorage.getItem('trackingBacklog');
      if (backlog) {
        const itemsMap = JSON.parse(backlog);
        Object.values(itemsMap).forEach(item => {
          this.sendBeacon(item);
        });
      }
      localStorage.setItem('trackingBacklog', JSON.stringify({}));
    } catch (e) {
      // ignore the errors, e.g. quota is full or security error
      console.error(e);
    }
  }

  track(
    eventName: TrackingEventName,
    eventData: TrackingEventData,
  ): Promise<void> {
    return this.auth.authState
      .switchMap(auth => {
        const e: TrackingEvent = {
          ...{
            acct: window.location.hostname.replace(/^www/, ''),
            t: Date.now().toString(),
            en: eventName,
            location: window.location.href,
            user_id: auth ? parseInt(auth.userId, 10) : undefined,
            session_id: this.sessionId,
          },
          ...eventData,
        };
        return this.sendBeacon(e);
      })
      .toPromise();
  }

  private generateSessionId(): string {
    const s4 = () =>
      Math.floor((1 + Math.random()) * 0x10000)
        .toString(16)
        .substring(1);
    return `${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
  }

  private sendBeacon(payload: TrackingEvent): Promise<void> {
    const beaconId = this.sequenceNum++;

    // save the event for later in case of a hard navigation
    try {
      const backlog = JSON.parse(
        <string>localStorage.getItem('trackingBacklog') || '{}',
      );
      backlog[beaconId] = payload;
      localStorage.setItem('trackingBacklog', JSON.stringify(backlog));
    } catch (e) {
      // ignore the errors, e.g. quota is full or security error
      console.error(e);
    }

    return (
      Observable.of(payload)
        // this prevents cancelling in-flight requests in case a user_action
        // event triggers a hard navigation
        .delay(100)
        .switchMap(e => this.http.post(this.config.trackingEndpoint, e))
        .do(() => {
          // Remove the event from the backlog once it has been sent
          try {
            const backlog2 = JSON.parse(
              <string>localStorage.getItem('trackingBacklog') || '{}',
            );
            delete backlog2[beaconId];
            localStorage.setItem('trackingBacklog', JSON.stringify(backlog2));
          } catch (e) {
            // ignore the errors, e.g. quota is full or security error
            console.error(e);
          }
        })
        .mapTo(undefined)
        .toPromise()
        .catch((err: HttpErrorResponse) => {
          if (err.error instanceof Error) {
            // A client-side or network error occurred.
            throw err.error;
          } else {
            // The backend returned an unsuccessful response code
            throw err.message;
          }
        })
    );
  }
}



// WEBPACK FOOTER //
// /mnt/jenkins-slave-workspace/workspace/gaf-build-i18n-webapp-compat/LANG/es/webapp/src/app/@shared/tracking/tracking.service.ts